/* ============================================
   THEME.JS — Core Theme JavaScript
   Band Custom Theme
   ============================================ */

'use strict';

// ---- Cart Drawer ----
class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('.cart-drawer');
    this.overlay = document.querySelector('.cart-drawer__overlay');
    this.closeBtn = document.querySelector('.cart-drawer__close');
    this.cartCount = document.querySelectorAll('[data-cart-count]');
    if (!this.drawer) return;
    this.bindEvents();
  }

  bindEvents() {
    document.querySelectorAll('[data-open-cart]').forEach(btn => {
      btn.addEventListener('click', () => this.open());
    });
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
    this.fetchCart();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-visible');
    document.body.style.overflow = '';
  }

  async fetchCart() {
    const res = await fetch('/cart.json');
    const cart = await res.json();
    this.renderCart(cart);
    this.updateCartCount(cart.item_count);
  }

  renderCart(cart) {
    const body = this.drawer.querySelector('.cart-drawer__body');
    const footer = this.drawer.querySelector('.cart-drawer__footer');
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = '<p style="text-align:center;padding:4rem 0;opacity:0.5;font-size:1.4rem;">Your cart is empty</p>';
      footer.innerHTML = '';
      return;
    }

    body.innerHTML = cart.items.map(item => `
      <div class="cart-item">
        <img class="cart-item__image" src="${item.image}" alt="${item.title}" width="80" height="80">
        <div>
          <div class="cart-item__title">${item.product_title}</div>
          ${item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
          <div class="cart-item__price">${this.formatMoney(item.final_line_price)}</div>
          <div style="display:flex;align-items:center;gap:1rem;margin-top:0.8rem;">
            <button class="qty-btn" data-action="decrease" data-id="${item.key}" style="width:2.8rem;height:2.8rem;border:1px solid var(--color-border);display:flex;align-items:center;justify-content:center;font-size:1.8rem;">-</button>
            <span style="font-size:1.4rem;min-width:2rem;text-align:center;">${item.quantity}</span>
            <button class="qty-btn" data-action="increase" data-id="${item.key}" style="width:2.8rem;height:2.8rem;border:1px solid var(--color-border);display:flex;align-items:center;justify-content:center;font-size:1.8rem;">+</button>
            <button class="qty-btn" data-action="remove" data-id="${item.key}" style="margin-left:auto;opacity:0.4;font-size:1.2rem;text-transform:uppercase;letter-spacing:0.06em;">Remove</button>
          </div>
        </div>
      </div>
    `).join('');

    footer.innerHTML = `
      <div class="cart-totals">
        <span>Total</span>
        <span>${this.formatMoney(cart.total_price)}</span>
      </div>
      <a href="/checkout" class="btn btn--primary" style="width:100%;justify-content:center;">Checkout</a>
      <a href="/cart" class="btn btn--secondary" style="width:100%;justify-content:center;margin-top:1rem;">View Cart</a>
    `;

    body.querySelectorAll('.qty-btn').forEach(btn => {
      btn.addEventListener('click', () => this.updateItem(btn.dataset.id, btn.dataset.action, cart));
    });
  }

  async updateItem(key, action, cart) {
    const item = cart.items.find(i => i.key === key);
    if (!item) return;
    let qty = item.quantity;
    if (action === 'increase') qty++;
    else if (action === 'decrease') qty = Math.max(0, qty - 1);
    else if (action === 'remove') qty = 0;

    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
    this.fetchCart();
  }

  updateCartCount(count) {
    this.cartCount.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
}

// ---- Mobile Menu ----
class MobileMenu {
  constructor() {
    this.menu = document.querySelector('.mobile-menu');
    this.openBtn = document.querySelector('[data-open-mobile-menu]');
    this.closeBtn = document.querySelector('[data-close-mobile-menu]');
    if (!this.menu) return;
    this.bindEvents();
  }

  bindEvents() {
    this.openBtn?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });
  }

  open() {
    this.menu.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.menu.classList.remove('is-open');
    document.body.style.overflow = '';
  }
}

// ---- Add to Cart ----
class AddToCart {
  constructor() {
    this.bindEvents();
  }

  bindEvents() {
    document.querySelectorAll('[data-add-to-cart]').forEach(form => {
      form.addEventListener('submit', async e => {
        e.preventDefault();
        const btn = form.querySelector('[type="submit"]');
        const originalText = btn.textContent;
        btn.textContent = 'Adding...';
        btn.disabled = true;

        const formData = new FormData(form);
        await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        btn.textContent = 'Added!';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1500);

        document.querySelector('.cart-drawer')?.dispatchEvent(new Event('open-cart'));
        window.cartDrawer?.fetchCart();
        window.cartDrawer?.open();
      });
    });
  }
}

// ---- Variant Selector ----
class VariantSelector {
  constructor() {
    this.bindEvents();
  }

  bindEvents() {
    document.querySelectorAll('[data-variant-select]').forEach(select => {
      select.addEventListener('change', e => {
        this.updateVariant(e.target.value);
      });
    });
  }

  updateVariant(variantId) {
    const productForm = document.querySelector('[data-add-to-cart]');
    if (!productForm) return;
    const input = productForm.querySelector('[name="id"]');
    if (input) input.value = variantId;

    fetch(`/products/${document.querySelector('[data-product-handle]')?.dataset.productHandle}.js`)
      .then(r => r.json())
      .then(product => {
        const variant = product.variants.find(v => v.id == variantId);
        if (!variant) return;

        const priceEl = document.querySelector('[data-variant-price]');
        if (priceEl) priceEl.textContent = '$' + (variant.price / 100).toFixed(2);

        const addBtn = document.querySelector('[data-add-to-cart] [type="submit"]');
        if (addBtn) {
          if (!variant.available) {
            addBtn.textContent = 'Sold Out';
            addBtn.disabled = true;
          } else {
            addBtn.textContent = 'Add to Cart';
            addBtn.disabled = false;
          }
        }

        const imgEl = document.querySelector('[data-variant-image]');
        if (imgEl && variant.featured_image) {
          imgEl.src = variant.featured_image.src;
        }
      });
  }
}

// ---- Quantity Input ----
class QuantityInput {
  constructor() {
    document.querySelectorAll('.quantity-input').forEach(wrapper => {
      const input = wrapper.querySelector('input');
      wrapper.querySelector('[data-minus]')?.addEventListener('click', () => {
        input.value = Math.max(1, parseInt(input.value) - 1);
      });
      wrapper.querySelector('[data-plus]')?.addEventListener('click', () => {
        input.value = parseInt(input.value) + 1;
      });
    });
  }
}

// ---- Init ----
document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();
  new MobileMenu();
  new AddToCart();
  new VariantSelector();
  new QuantityInput();
});
